import { useCallback } from 'react'
import { useDiscogsConnection } from '../context/DiscogsContext'
import { useDiscogsMembership } from './useDiscogsLists'
import { useDiscogsWriteGate } from '../components/DiscogsWriteGate'
import { useToast } from '../context/ToastContext'
import { formatReleaseLabel } from '../utils/release'
import { track } from '../utils/track'
import axiosClient from '../api/client'
import type { Release } from '../types/release'

/**
 * Single source of truth for Discogs wantlist/collection writes.
 *
 * Every call-site (release detail page, crate view, grids, …) should go
 * through this hook instead of hand-rolling `axios` + context mutations.
 * It handles: the teach-once confirmation gate, an optimistic local update,
 * the API call, rollback on failure, and the success/error toast.
 *
 * Returns `true` when the write succeeded, `false` when it was cancelled at
 * the gate, blocked (not connected), or failed on the server.
 */
export function useDiscogsWrite() {
  const { isAuthenticated } = useDiscogsConnection()
  const {
    isInWantlist, addToWantlist, removeFromWantlist,
    isInCollection, addToCollection, removeFromCollection,
  } = useDiscogsMembership()
  const { gate } = useDiscogsWriteGate()
  const { showToast } = useToast()

  const requireConnection = useCallback(() => {
    if (isAuthenticated) return true
    showToast('Connect your Discogs account in Settings first', 'warning')
    return false
  }, [isAuthenticated, showToast])

  const toggleWantlist = useCallback(async (release: Release): Promise<boolean> => {
    if (!requireConnection()) return false
    const inList = isInWantlist(release.id)
    const ok = await gate(inList ? 'remove-wantlist' : 'add-wantlist', { releaseLabel: formatReleaseLabel(release) })
    if (!ok) return false

    // Optimistic update — flip immediately, roll back if the server rejects.
    if (inList) removeFromWantlist(release.id)
    else addToWantlist(release.id)

    try {
      if (inList) {
        await axiosClient.delete(`/discogs/wantlist/${release.id}`)
      } else {
        await axiosClient.post('/discogs/wantlist', { releaseId: release.id })
        track('wantlist_add', { releaseId: release.id })
      }
      showToast(inList ? 'Removed from Discogs wantlist' : 'Added to Discogs wantlist', 'success')
      return true
    } catch (error: any) {
      if (inList) addToWantlist(release.id)
      else removeFromWantlist(release.id)
      showToast(error?.response?.data?.message || 'Failed to update wantlist', 'error')
      return false
    }
  }, [requireConnection, isInWantlist, addToWantlist, removeFromWantlist, gate, showToast])

  const toggleCollection = useCallback(async (release: Release): Promise<boolean> => {
    if (!requireConnection()) return false
    const inCollection = isInCollection(release.id)
    const ok = await gate(inCollection ? 'remove-collection' : 'add-collection', { releaseLabel: formatReleaseLabel(release) })
    if (!ok) return false

    if (inCollection) removeFromCollection(release.id)
    else addToCollection(release.id)

    try {
      if (inCollection) await axiosClient.delete(`/discogs/collection/${release.id}`)
      else await axiosClient.post('/discogs/collection', { releaseId: release.id })
      showToast(inCollection ? 'Removed from Discogs collection' : 'Added to Discogs collection', 'success')
      return true
    } catch (error: any) {
      if (inCollection) addToCollection(release.id)
      else removeFromCollection(release.id)
      showToast(error?.response?.data?.message || 'Failed to update collection', 'error')
      return false
    }
  }, [requireConnection, isInCollection, addToCollection, removeFromCollection, gate, showToast])

  return { toggleWantlist, toggleCollection }
}
